import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/AdminLayout';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiClock, FiPlay, FiPause, FiSettings, FiActivity, FiCheckCircle, FiRefreshCw, FiList } = FiIcons;

const Automations = () => {
  // Initialize state functions to load from localStorage immediately
  const [isRunning, setIsRunning] = useState(() => {
    const savedStatus = localStorage.getItem('ramirez_automation_status');
    return savedStatus ? JSON.parse(savedStatus) : true;
  });

  const [rules, setRules] = useState(() => {
    const savedRules = localStorage.getItem('ramirez_automation_rules');
    if (savedRules) {
      return JSON.parse(savedRules);
    }
    return [
      { id: 1, name: "90-Day Warning", days: 90, active: true, type: "Email" },
      { id: 2, name: "30-Day Alert", days: 30, active: true, type: "SMS & Email" },
      { id: 3, name: "15-Day Urgent", days: 15, active: true, type: "SMS" },
      { id: 4, name: "7-Day Critical", days: 7, active: true, type: "SMS & Call" },
      { id: 5, name: "Expiration Notice", days: 0, active: true, type: "All Channels" },
    ];
  });

  const [lastRun, setLastRun] = useState('Never');
  const [processing, setProcessing] = useState(false);
  
  // Mock Queue Data (Persisted)
  const [queue, setQueue] = useState([]);
  const [logs, setLogs] = useState([]);

  useEffect(() => {
    // Load Logs
    const storedLogs = localStorage.getItem('ramirez_logs');
    if (storedLogs) setLogs(JSON.parse(storedLogs));
    else setLogs([{ id: 1, time: "System Start", action: "System Initialized", result: "Ready" }]);

    // Initialize Mock Queue
    setQueue([
      { id: 101, company: "Rivera Transport", vehicle: "Truck #42", due: "2024-04-15", rule: "30-Day Alert", status: "Pending" },
      { id: 102, company: "Smith Logistics", vehicle: "Fleet A", due: "2024-03-28", rule: "7-Day Critical", status: "Pending" },
      { id: 103, company: "MJ Trucking", vehicle: "Unit 3", due: "2024-05-10", rule: "90-Day Warning", status: "Scheduled" },
    ]);
  }, []);

  // Save Rules whenever they change
  useEffect(() => {
    localStorage.setItem('ramirez_automation_rules', JSON.stringify(rules));
  }, [rules]);

  // Save System Status whenever it changes
  useEffect(() => {
    localStorage.setItem('ramirez_automation_status', JSON.stringify(isRunning));
  }, [isRunning]);

  const saveLog = (newLog) => {
      const updatedLogs = [newLog, ...logs].slice(0, 50); // Keep last 50
      setLogs(updatedLogs);
      localStorage.setItem('ramirez_logs', JSON.stringify(updatedLogs));
  }

  const toggleRule = (id) => {
    setRules(prevRules => prevRules.map(rule =>
      rule.id === id ? { ...rule, active: !rule.active } : rule
    ));
  };

  const toggleSystem = () => {
    setIsRunning(prev => !prev);
  };

  const runManualCheck = () => {
    if (queue.length === 0) return;

    setProcessing(true);
    setTimeout(() => {
      setProcessing(false);
      const now = new Date().toLocaleString();
      setLastRun(now);

      const processedItem = queue[0];
      if (processedItem) {
        // Log the action
        const newLog = {
          id: Date.now(),
          time: new Date().toLocaleTimeString(),
          action: `Processed ${processedItem.rule}`,
          result: `Sent to ${processedItem.company}`
        };
        saveLog(newLog);

        // Remove from queue
        setQueue(prev => prev.slice(1));

        // Trigger Admin Notification
        const storedNotifs = JSON.parse(localStorage.getItem('adminNotifications') || '[]');
        const newNotif = {
          id: Date.now(),
          title: 'Automated Alert Sent',
          message: `Successfully sent ${processedItem.rule} to ${processedItem.company} for ${processedItem.vehicle}.`,
          type: 'success',
          time: 'Just now',
          read: false
        };
        localStorage.setItem('adminNotifications', JSON.stringify([newNotif, ...storedNotifs]));
      }
    }, 1500);
  };

  return (
    <AdminLayout>
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white mb-2">Automation Control Center</h1>
          <p className="text-gray-400">Manage automated compliance reminders and notification schedules.</p>
        </div>
        <div className="flex items-center gap-4">
          <div className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors ${isRunning ? 'bg-green-900/20 border-green-800' : 'bg-red-900/20 border-red-800'}`}>
            <div className={`w-3 h-3 rounded-full ${isRunning ? 'bg-green-500 animate-pulse' : 'bg-red-500'}`}></div>
            <span className={`text-sm font-medium ${isRunning ? 'text-green-400' : 'text-red-400'}`}>
              {isRunning ? 'System Active' : 'System Paused'}
            </span>
          </div>
          <button
            onClick={toggleSystem}
            className={`p-2 rounded-lg border transition-all transform active:scale-95 ${isRunning ? 'bg-red-900/20 border-red-800 text-red-500 hover:bg-red-900/40' : 'bg-green-900/20 border-green-800 text-green-500 hover:bg-green-900/40'}`}
            title={isRunning ? "Pause Automations" : "Resume Automations"}
          >
            <SafeIcon icon={isRunning ? FiPause : FiPlay} className="text-xl" />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Rules Configuration */}
        <div className="lg:col-span-2 space-y-8">
          {/* Rules Card */}
          <div className="bg-gray-800 border border-gray-700 rounded-xl p-6 shadow-xl">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-lg font-bold text-white flex items-center gap-2">
                <SafeIcon icon={FiSettings} className="text-blue-500" />
                Notification Rules
              </h2>
              <span className="text-xs text-gray-500">Triggers based on Expiry Date</span>
            </div>
            <div className="space-y-4">
              {rules.map((rule) => (
                <div key={rule.id} className={`flex items-center justify-between p-4 rounded-lg transition-all border ${rule.active ? 'bg-gray-700/50 border-gray-600' : 'bg-gray-800/50 border-gray-700 opacity-75'}`}>
                  <div className="flex items-center gap-4">
                    <div className={`p-2 rounded-lg transition-colors ${rule.active ? 'bg-blue-600/20 text-blue-400' : 'bg-gray-700 text-gray-500'}`}>
                      <SafeIcon icon={FiClock} />
                    </div>
                    <div>
                      <h4 className={`font-bold transition-colors ${rule.active ? 'text-white' : 'text-gray-500'}`}>{rule.name}</h4>
                      <p className="text-xs text-gray-400">{rule.days} days before expiry • via {rule.type}</p>
                    </div>
                  </div>
                  
                  {/* Toggle Switch */}
                  <button
                    onClick={() => toggleRule(rule.id)}
                    className={`relative w-14 h-7 rounded-full transition-colors duration-300 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-900 focus:ring-blue-500 ${rule.active ? 'bg-green-600' : 'bg-gray-600'}`}
                    aria-pressed={rule.active}
                  >
                    <span className="sr-only">Toggle {rule.name}</span>
                    <span
                      className={`absolute top-1 left-1 bg-white w-5 h-5 rounded-full shadow-md transform transition-transform duration-300 ease-in-out ${rule.active ? 'translate-x-7' : 'translate-x-0'}`}
                    />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Queue Card */}
          <div className="bg-gray-800 border border-gray-700 rounded-xl p-6 shadow-xl">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-lg font-bold text-white flex items-center gap-2">
                <SafeIcon icon={FiList} className="text-yellow-500" />
                Upcoming Queue
              </h2>
              <button
                onClick={runManualCheck}
                disabled={processing || !isRunning || queue.length === 0}
                className={`text-sm px-3 py-1.5 rounded-lg border flex items-center gap-2 transition-all ${processing || queue.length === 0 || !isRunning ? 'bg-gray-700 text-gray-400 border-gray-600 cursor-not-allowed' : 'bg-blue-600 text-white border-blue-500 hover:bg-blue-700'}`}
              >
                <SafeIcon icon={FiRefreshCw} className={processing ? 'animate-spin' : ''} />
                {processing ? 'Processing...' : 'Run Manual Check'}
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="text-gray-400 text-xs uppercase border-b border-gray-700">
                    <th className="pb-3 pl-2">Company</th>
                    <th className="pb-3">Trigger</th>
                    <th className="pb-3">Due Date</th>
                    <th className="pb-3">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-700">
                  {queue.length > 0 ? (
                    queue.map((item) => (
                      <tr key={item.id} className="text-sm">
                        <td className="py-3 pl-2 font-medium text-white">{item.company}</td>
                        <td className="py-3 text-blue-400">{item.rule}</td>
                        <td className="py-3 text-gray-400">{item.due}</td>
                        <td className="py-3">
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-900/30 text-yellow-500 border border-yellow-800">
                            {item.status}
                          </span>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="4" className="py-8 text-center text-gray-500">
                        <div className="flex flex-col items-center gap-2">
                          <SafeIcon icon={FiCheckCircle} className="text-2xl opacity-50 text-green-500" />
                          <span>Queue is empty. All caught up!</span>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* System Status & Logs */}
        <div className="space-y-8">
          <div className="bg-gray-800 border border-gray-700 rounded-xl p-6 shadow-xl">
            <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
              <SafeIcon icon={FiActivity} className="text-green-500" />
              System Status
            </h2>
            <div className="space-y-4">
              <div className="p-4 bg-gray-900/50 rounded-lg border border-gray-700 flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-400 mb-1">Last Successful Run</p>
                  <p className="text-white font-mono text-sm">{lastRun}</p>
                </div>
                <SafeIcon icon={FiCheckCircle} className="text-green-500" />
              </div>
              <div className="p-4 bg-gray-900/50 rounded-lg border border-gray-700 flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-400 mb-1">Next Scheduled Run</p>
                  <p className="text-white font-mono text-sm">Today, 06:00 PM</p>
                </div>
                <SafeIcon icon={FiClock} className="text-blue-500" />
              </div>
            </div>
          </div>

          <div className="bg-gray-800 border border-gray-700 rounded-xl p-6 shadow-xl">
            <h2 className="text-lg font-bold text-white mb-4">Activity Log</h2>
            <div className="space-y-4 max-h-[300px] overflow-y-auto pr-2 custom-scrollbar">
              {logs.map((log) => (
                <div key={log.id} className="border-l-2 border-gray-600 pl-4 py-1 relative">
                  <div className="absolute -left-[5px] top-2 w-2 h-2 rounded-full bg-gray-500"></div>
                  <p className="text-xs text-gray-400">{log.time}</p>
                  <p className="text-sm font-medium text-white">{log.action}</p>
                  <p className="text-xs text-gray-500">{log.result}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default Automations;